import React, { useState, useRef, useCallback } from "react";
import { useAuth } from "../App";
import {
  Shield, BookOpen, FileText, Eye, DollarSign, Briefcase, ExternalLink,
  Search, Sparkles, Printer, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { statusColor } from "../components/ui/status-colors";
import { ROLE_GUIDES, SuperuserGuide } from "../components/help/RoleGuides";

const ROLES = [
  { id: "superuser", label: "Superuser", icon: Shield, color: "bg-red-100 text-red-700", desc: "Full system access including user management, audit logs, feature toggles, and all features." },
  { id: "admin", label: "Admin", icon: Shield, color: "bg-orange-100 text-orange-700", desc: "System administration, user role management, approval policies, AI usage management, and all operational features." },
  { id: "legal", label: "Legal", icon: BookOpen, color: "bg-blue-100 text-blue-700", desc: "Template and clause management, contract review, compliance monitoring, and supplier review." },
  { id: "contract_creator", label: "Contract Creator", icon: FileText, color: "bg-emerald-100 text-emerald-700", desc: "Draft new contracts, submit for review, manage contract lifecycle, track compliance." },
  { id: "contract_owner", label: "Contract Owner", icon: Eye, color: "bg-slate-100 text-slate-700", desc: "View-only access to assigned contracts. Cannot draft or edit contracts." },
  { id: "finance", label: "Finance", icon: DollarSign, color: "bg-amber-100 text-amber-700", desc: "View contracts, access reports, monitor compliance, and approve financial matters." },
  { id: "executive", label: "Executive", icon: Briefcase, color: "bg-purple-100 text-purple-700", desc: "High-level contract visibility, reports access, and final approval authority." },
  { id: "external", label: "External Reviewer", icon: ExternalLink, color: "bg-violet-100 text-violet-700", desc: "Review contracts or notices via secure guest link. No login required." },
];

const LIFECYCLE_STAGES = [
  { label: "Draft", status: "draft" },
  { label: "Under Review", status: "review" },
  { label: "Pending Approval", status: "pending" },
  { label: "Approved", status: "approved" },
  { label: "Active", status: "active" },
  { label: "Expired / Terminated / Archived", status: "rejected" },
];

const QUICK_REF = [
  { title: "Segregation of Duty", text: "Contract creator cannot be their own approver." },
  { title: "Access Control", text: "Post-approval contracts are restricted to assigned users only." },
  { title: "External Review", text: "Guest links allow secure review without system access." },
  { title: "AI Features", text: "Clause review, risk analysis, obligation extraction, benchmarking, decision intelligence, notice generation." },
  { title: "Approval Routing", text: "Attribute-based policies auto-assign approvers by contract value, type, category, department, and jurisdiction." },
  { title: "Renewal Alerts", text: "Automatic expiry and deadline notifications at 90/60/30/14/7/3/1 day thresholds." },
  { title: "User Invitations", text: "Invite via email with 72-hour expiry and resend capability." },
  { title: "Clause Benchmarking", text: "AI scores clauses across 6 criteria with improvement suggestions." },
  { title: "Seat Management", text: "Plan-based user limits (Starter: 15, Professional: 50, Enterprise: 999)." },
  { title: "Security", text: "HttpOnly cookie auth, MFA for superusers, AI rate limiting (30 req/min), global audit logging." },
  { title: "Feature Toggles", text: "Superusers can disable/enable individual features per org, overriding plan defaults." },
  { title: "Multi-Tenancy", text: "Collection-prefixed isolation per organisation. Holding/Subsidiary/Branch hierarchy. Per-entity RBAC." },
  { title: "Bulk Import", text: "CSV and Excel upload. Small files instant, large files run in background with progress tracking." },
  { title: "AI Quota", text: "Three-tier enforcement: burst (30/min), daily per-user, monthly tenant quota. Alerts at 80% and 95%." },
];

export default function HelpPage() {
  const { user, startTour } = useAuth();
  const [activeRole, setActiveRole] = useState(user?.role || "superuser");
  const [search, setSearch] = useState("");
  const printRef = useRef(null);

  const handlePrint = useCallback(() => { window.print(); }, []);

  const handleDownloadHTML = useCallback(() => {
    const el = printRef.current;
    if (!el) return;
    const roleInfo = ROLES.find(r => r.id === activeRole);
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>ConTrack Help Guide - ${roleInfo?.label || activeRole}</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:900px;margin:0 auto;padding:40px 20px;color:#1e293b}
h1{font-size:28px;border-bottom:2px solid #e2e8f0;padding-bottom:12px}h2{font-size:20px;margin-top:32px;color:#334155}
img{max-width:100%;border:1px solid #e2e8f0;border-radius:8px;margin:12px 0}
.step{display:flex;gap:12px;margin:12px 0}.step-num{width:28px;height:28px;border-radius:50%;background:#e0e7ff;color:#4338ca;display:flex;align-items:center;justify-content:center;font-weight:700;flex-shrink:0}
p{line-height:1.7;color:#475569}</style></head>
<body><h1>ConTrack User Guide: ${roleInfo?.label || activeRole}</h1><p><em>${roleInfo?.desc || ""}</em></p><hr/>
${el.innerHTML}</body></html>`;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ConTrack_Help_Guide_${activeRole}.html`; a.click();
    URL.revokeObjectURL(url);
  }, [activeRole]);

  const handleDownloadPDF = useCallback(async () => {
    const el = printRef.current;
    if (!el) return;
    const html2canvas = (await import("html2canvas")).default;
    const { jsPDF } = await import("jspdf");
    const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, windowWidth: 900 });
    const imgData = canvas.toDataURL("image/jpeg", 0.85);
    const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, "JPEG", 0, 0, canvas.width, canvas.height);
    pdf.save(`ConTrack_Help_Guide_${activeRole}.pdf`);
  }, [activeRole]);

  const GuideComponent = ROLE_GUIDES[activeRole] || SuperuserGuide;
  const activeRoleInfo = ROLES.find(r => r.id === activeRole);

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Help & User Guide</h1>
          <p className="text-slate-500 text-sm mt-1">Step-by-step instructions for every role in ConTrack</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2 text-xs" onClick={() => { startTour(); }} data-testid="restart-tour-btn"><Sparkles size={14} /> Restart Tour</Button>
          <Button variant="outline" className="rounded-sm gap-2 text-xs" onClick={handlePrint} data-testid="print-guide-btn"><Printer size={14} /> Print</Button>
          <Button variant="outline" className="rounded-sm gap-2 text-xs" onClick={handleDownloadHTML} data-testid="download-html-btn"><Download size={14} /> Download HTML</Button>
          <Button className="rounded-sm gap-2 text-xs bg-slate-900 hover:bg-slate-800" onClick={handleDownloadPDF} data-testid="download-pdf-btn"><FileText size={14} /> Download PDF</Button>
        </div>
      </div>

      {/* Role Selector */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map(role => (
          <Button key={role.id} variant={activeRole === role.id ? "default" : "outline"}
            className={`rounded-sm gap-2 text-xs h-9 ${activeRole === role.id ? "bg-slate-900" : ""}`}
            onClick={() => setActiveRole(role.id)} data-testid={`role-tab-${role.id}`}>
            <role.icon size={14} /> {role.label}
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search within this guide..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="help-search" />
      </div>

      {/* Role Overview Card */}
      <Card className="border-l-4 border-l-indigo-500">
        <CardContent className="py-4 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${activeRoleInfo?.color || "bg-slate-100"}`}>
            {activeRoleInfo && <activeRoleInfo.icon size={24} />}
          </div>
          <div>
            <h2 className="font-semibold text-lg text-slate-800">{activeRoleInfo?.label} Guide</h2>
            <p className="text-sm text-slate-500">{activeRoleInfo?.desc}</p>
          </div>
        </CardContent>
      </Card>

      {/* Guide Content */}
      <div ref={printRef} className="guide-content"><GuideComponent /></div>

      {/* Quick Reference */}
      <Card className="mt-8">
        <CardHeader><CardTitle className="text-base">Quick Reference: Contract Lifecycle</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {LIFECYCLE_STAGES.map((stage, i, arr) => (
              <React.Fragment key={stage.label}>
                <Badge className={`rounded-sm ${statusColor(stage.status)}`}>{stage.label}</Badge>
                {i < arr.length - 1 && <span className="text-slate-300">&rarr;</span>}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {QUICK_REF.map(item => (
              <div key={item.title} className="p-2 border rounded-sm"><strong>{item.title}:</strong> {item.text}</div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`@media print { nav, header, .sidebar, [data-testid="print-guide-btn"], [data-testid="download-html-btn"], [data-testid="download-pdf-btn"], [data-testid="help-search"], button[data-testid^="role-tab-"] { display: none !important; } .guide-content { max-width: 100% !important; } figure img { max-height: 400px; object-fit: contain; } }`}</style>
    </div>
  );
}
